import { ChevronRight } from 'lucide-react';

const money = (value: number) =>
  `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * A proposal or invoice line on a phone or tablet, the same on both: what it is (two lines at
 * most), then one quiet line with its department, quantity × price and any state in words, and
 * the total on the right. Tapping opens the line sheet.
 */
export const LineItemRow = ({
  description,
  department,
  quantity,
  unitPrice,
  discount = 0,
  notes = [],
  onOpen,
}: {
  description: string;
  department?: string;
  quantity: number;
  unitPrice: number;
  discount?: number;
  /** States worth saying on the line: "Pending", "Not on invoice". */
  notes?: string[];
  /** Without it the row is read-only (view-only proposals, locked payments). */
  onOpen?: () => void;
}) => {
  const meta = [department, `${quantity} × ${money(unitPrice)}`, ...notes]
    .filter(Boolean)
    .join(' · ');
  const body = (
    <>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="line-clamp-2 break-words text-sm font-medium">{description}</span>
        <span className="text-muted-foreground text-xs tabular-nums">{meta}</span>
      </span>
      <span className="flex shrink-0 flex-col items-end gap-0.5">
        <span className="text-sm font-semibold tabular-nums">{money(unitPrice * quantity)}</span>
        {discount > 0 && (
          <span className="text-success-foreground text-xs tabular-nums">−{money(discount)}</span>
        )}
      </span>
      {/* The arrow's space is kept on read-only rows too, so amounts line up with the totals. */}
      {onOpen ? (
        <ChevronRight className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      ) : (
        <span className="w-4 shrink-0" aria-hidden />
      )}
    </>
  );
  return onOpen ? (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Edit ${description}`}
      className="active:bg-muted flex min-h-14 w-full items-start gap-3 px-4 py-3 text-left"
    >
      {body}
    </button>
  ) : (
    <div className="flex min-h-14 items-start gap-3 px-4 py-3">{body}</div>
  );
};
